import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {HandelspartnerResource} from '@services/resource/handelspartner-resource';
import {HandelspartnerService} from '@services/handelspartner.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {BehaviorSubject} from 'rxjs';
import {VoilaUserService} from '@services/voila-user.service';
import {ordString} from 'fp-ts/lib/Ord';
import {sort} from 'fp-ts/lib/Array';
import {allHandelspartners} from '@app/data/handelspartner-data';
import {CompanyService} from '@services/company.service';
import {MatPaginator} from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import {SearchInputFieldComponent} from '@widgets/search-input-field/search-input-field.component';
import {MarkantElementsModule} from '@markant/elements-angular';
import {MatIcon} from '@angular/material/icon';
import {EditUserComponent} from '@ma-dashboard/users/edit/edit-user.component';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'app-handelspartner',
  templateUrl: './handelspartner.component.html',
  styleUrls: ['./handelspartner.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SearchInputFieldComponent,
    MatTable,
    MarkantElementsModule,
    MatIcon,
    MatPaginator,
    EditUserComponent,
    MatCellDef,
    MatRowDef,
    MatHeaderRowDef,
    MatHeaderCellDef,
    MatSort,
    MatSortHeader,
    MatHeaderRow,
    MatRow,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
  ],
})
export class HandelspartnerComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  public displayedColumns = ['name', 'mnetId', 'companyName', 'permissions', 'slug'];
  allSlugs = sort(ordString)(allHandelspartners);

  slugEditable = false;
  dialogState = false;
  handelspartner: HandelspartnerResource[];
  handelspartnerView: MatTableDataSource<HandelspartnerResource> = new MatTableDataSource<HandelspartnerResource>();
  selectedUser: HandelspartnerResource | undefined;
  private searchQuery = '';
  private readonly loadHandelspartner$: BehaviorSubject<void> = new BehaviorSubject(undefined as void);

  constructor(
    private readonly handelspartnerService: HandelspartnerService,
    private readonly userService: VoilaUserService,
    private readonly companyService: CompanyService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.loadHandelspartner$.subscribe(() => {
      this.handelspartnerService.getHandelspartner().subscribe((handelspartner) => {
        this.handelspartner = handelspartner;
        this.handelspartnerView.data = this.getHandelspartnerView(handelspartner, this.searchQuery);
      });
    });
  }

  filterHandelspartner(searchQuery: string | null | undefined): void {
    this.searchQuery = searchQuery ?? '';
    if (this.handelspartner) {
      this.handelspartnerView.data = this.getHandelspartnerView(this.handelspartner, this.searchQuery);
    }
  }

  private getHandelspartnerView(handelspartner: HandelspartnerResource[], searchQuery: string): HandelspartnerResource[] {
    if (searchQuery.length === 0) {
      return handelspartner;
    }
    const query = searchQuery.toLowerCase();
    return handelspartner.filter((hp) => {
      const searchIn = hp.firstName + hp.lastName + hp.companyName;
      return searchIn.toLowerCase().includes(query);
    });
  }

  ngAfterViewInit() {
    this.handelspartnerView.paginator = this.paginator;
    this.handelspartnerView.sortingDataAccessor = (hpRessource, column) => this.getSortValue(hpRessource, column);
    this.handelspartnerView.sort = this.sort;
  }

  private getSortValue(h: HandelspartnerResource, column: string): string | number {
    if (column === 'name') {
      return `${h.firstName} ${h.lastName}`.toLowerCase();
    }
    if (column === 'permissions') {
      return this.hasVotingRight(h) ? 1 : 0;
    }
    const value = h[column as keyof HandelspartnerResource];
    return typeof value === 'string' ? value.toLowerCase() : '';
  }

  hasVotingRight(h: HandelspartnerResource): boolean {
    return h.permissions.indexOf('vote') >= 0;
  }

  private addVotingRight(h: HandelspartnerResource): void {
    this.userService.givePermission(h.id, 'vote').subscribe(() => {
      const msg = `Benutzer ${h.firstName} ${h.lastName} (${h.companyName}) wurden Abstimmrechte zugeteilt.`;
      this.snackBar.open(msg, 'Schließen', {duration: 5000});
      this.loadHandelspartner$.next();
    });
  }

  private removeVotingRight(h: HandelspartnerResource): void {
    this.userService.removePermission(h.id, 'vote').subscribe(() => {
      const msg = `Benutzer ${h.firstName} ${h.lastName} (${h.companyName}) wurden Abstimmrechte entfernt.`;
      this.snackBar.open(msg, 'Schließen', {duration: 5000});
      this.loadHandelspartner$.next();
    });
  }

  toggleVotingRight(h: HandelspartnerResource, event: any): void {
    if (event.detail) {
      this.addVotingRight(h);
    } else {
      this.removeVotingRight(h);
    }
  }

  setSlug(hp: HandelspartnerResource, e: any) {
    const newSlug = e.detail as string | null;
    this.companyService.setSlug(hp.companyId, newSlug).subscribe(() => {
      this.snackBar.open(`${hp.companyName} ist jetzt '${newSlug}' zugeordnet`);
      this.loadHandelspartner$.next();
    });
  }

  toggleSlugEditable(event: Event) {
    event.stopPropagation();
    this.slugEditable = !this.slugEditable;
  }

  closeDialog() {
    this.selectedUser = undefined;
    this.dialogState = false;
  }

  openUserModal(h: any) {
    this.selectedUser = h;
    this.dialogState = true;
  }
}
